import type {
  CombatLogEntry,
  CombatRollInfo,
  CombatState,
  EnemyInstance,
  EnemyTemplate,
  PlayerAction,
  PlayerCombatState,
} from './types';
import { obtenerEnemigo } from './enemyData';
import { createRng, rollCheck } from '@/utils/rng';
import { useCharacterStore } from '@/engine/state';
import { castearHechizo, obtenerHechizo } from '@/engine/sorcery';
import { decidirAccionEnemigo } from './ai';

/** Adapta el resultado de rollCheck (inglés) a CombatRollInfo (español). */
function tirarCheck(
  rng: () => number,
  modificador: number,
  dc: number,
): CombatRollInfo {
  const r = rollCheck(rng, modificador, dc);
  return {
    tirada: r.roll,
    modificador,
    total: r.total,
    dc,
    exito: r.success,
    crit: r.critical,
  };
}

/** RNG compartido para una instancia de combate. */
let rngActual: () => number = Math.random;

export function inicializarRng(seed?: number): void {
  rngActual = seed !== undefined ? createRng(seed) : Math.random;
}

/** Crea la instancia de combate inicial desde el store global. */
export function iniciarCombate(enemyId: string, seed?: number): CombatState {
  inicializarRng(seed);
  const template = obtenerEnemigo(enemyId);
  if (!template) throw new Error(`Enemigo inexistente: ${enemyId}`);

  const enemy = crearEnemy(template);
  const player = snapshotPlayer();

  return {
    enemy,
    player,
    turno: 'player',
    ronda: 1,
    log: [
      {
        id: `log-${Date.now()}-inicio`,
        ronda: 1,
        actor: 'enemy',
        accion: 'Aparece',
        resultado: `${template.nombre} te cierra el paso.`,
      },
    ],
    estado: 'activo',
  };
}

function crearEnemy(t: EnemyTemplate): EnemyInstance {
  return {
    ...t,
    vidaActual: t.vida,
    defensaTemp: 0,
    especialDisponibleEn: 1,
  };
}

function snapshotPlayer(): PlayerCombatState {
  const p = useCharacterStore.getState().personaje;
  return {
    vida: p.stats.vida,
    vidaMax: p.stats.vidaMax,
    mana: p.stats.mana,
    manaMax: p.stats.manaMax,
    stats: { ...p.stats },
    defensaTemp: 0,
    efectosTemporales: [],
  };
}

/** Calcula el mod de ataque del player: valentía + bonus por skills implícitos. */
function modAtaquePlayer(p: PlayerCombatState): number {
  return Math.floor(p.stats.valentia / 2) + 2;
}

function modDefensaPlayer(p: PlayerCombatState): number {
  return 10 + Math.floor(p.stats.resistencia / 2) + p.defensaTemp;
}

function tirarDano(min: number, max: number): number {
  return Math.floor(rngActual() * (max - min + 1)) + min;
}

function consumirEfectos(p: PlayerCombatState): PlayerCombatState {
  const nuevosEfectos = p.efectosTemporales
    .map((e) => ({ ...e, duracionRondas: e.duracionRondas - 1 }))
    .filter((e) => e.duracionRondas > 0);
  return { ...p, efectosTemporales: nuevosEfectos, defensaTemp: 0 };
}

function aplicarReduccionDano(p: PlayerCombatState, dano: number): number {
  let final = dano;
  for (const ef of p.efectosTemporales) {
    if (ef.efecto.kind === 'reduccionDanoEntrante') {
      final = Math.ceil(final * ef.efecto.factor);
    }
  }
  return final;
}

function nuevoLogId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.floor(rngActual() * 1000000)}`;
}

/** Resuelve una acción del player. Devuelve el nuevo estado (inmutable). */
export function ejecutarAccionPlayer(
  state: CombatState,
  action: PlayerAction,
): CombatState {
  if (state.estado !== 'activo' || state.turno !== 'player') {
    throw new Error('No es turno del player o el combate terminó.');
  }

  let nuevo: CombatState = { ...state, log: [...state.log] };

  switch (action.kind) {
    case 'atacar':
      nuevo = resolverAtaquePlayer(nuevo);
      break;
    case 'defender':
      nuevo = resolverDefenderPlayer(nuevo);
      break;
    case 'hechizo':
      nuevo = resolverHechizoPlayer(nuevo, action.spellId);
      break;
    case 'huir':
      nuevo = resolverHuidaPlayer(nuevo);
      break;
  }

  if (nuevo.estado !== 'activo') return nuevo;

  // Turno del enemigo
  nuevo = { ...nuevo, turno: 'enemy' };
  nuevo = resolverTurnoEnemigo(nuevo);

  if (nuevo.estado !== 'activo') return nuevo;

  // Nueva ronda
  nuevo = {
    ...nuevo,
    turno: 'player',
    ronda: nuevo.ronda + 1,
    player: consumirEfectos(nuevo.player),
    enemy: { ...nuevo.enemy, defensaTemp: 0 },
  };

  return nuevo;
}

function resolverAtaquePlayer(state: CombatState): CombatState {
  const { player, enemy } = state;
  const mod = modAtaquePlayer(player);
  let check = tirarCheck(rngActual, mod, enemy.armadura + enemy.defensaTemp);

  // Ventaja por efecto temporal (p. ej. Golpe Calculado)
  const ventaja = player.efectosTemporales.find(
    (e) => e.efecto.kind === 'ventajaProximoAtaque',
  );
  if (ventaja) {
    const segundo = tirarCheck(rngActual, mod, enemy.armadura + enemy.defensaTemp);
    if (segundo.total > check.total) check = segundo;
  }

  const entry: CombatLogEntry = {
    id: nuevoLogId('atk-p'),
    ronda: state.ronda,
    actor: 'player',
    accion: 'Ataque',
    resultado: '',
    roll: check,
  };

  if (!check.exito) {
    entry.resultado = check.crit === 'miss' ? 'Fallaste mal. Quedás abierto.' : 'Tu golpe no conecta.';
    return { ...state, log: [...state.log, entry] };
  }

  let dano = tirarDano(4, 10) + Math.floor(player.stats.valentia / 2);
  if (check.crit === 'hit') dano *= 2;

  const nuevaVidaEnemy = Math.max(0, enemy.vidaActual - dano);
  entry.resultado =
    check.crit === 'hit'
      ? `¡Crítico! Le hacés ${dano} de daño.`
      : `Conectás: ${dano} de daño.`;
  entry.delta = { vidaEnemy: -dano };

  const nuevoState: CombatState = {
    ...state,
    enemy: { ...enemy, vidaActual: nuevaVidaEnemy },
    log: [...state.log, entry],
    // Consumir la ventaja si la usó
    player: ventaja
      ? {
          ...player,
          efectosTemporales: player.efectosTemporales.filter(
            (e) => e.id !== ventaja.id,
          ),
        }
      : player,
  };

  if (nuevaVidaEnemy === 0) {
    return finalizar(nuevoState, 'victoria');
  }
  return nuevoState;
}

function resolverDefenderPlayer(state: CombatState): CombatState {
  const entry: CombatLogEntry = {
    id: nuevoLogId('def-p'),
    ronda: state.ronda,
    actor: 'player',
    accion: 'Defender',
    resultado: 'Te plantás. +4 a tu armadura este turno.',
  };
  return {
    ...state,
    player: { ...state.player, defensaTemp: state.player.defensaTemp + 4 },
    log: [...state.log, entry],
  };
}

function resolverHechizoPlayer(state: CombatState, spellId: string): CombatState {
  const spell = obtenerHechizo(spellId);
  if (!spell) {
    throw new Error(`Hechizo inexistente: ${spellId}`);
  }

  let estadoConMana: CombatState = state;
  try {
    castearHechizo(spellId, 'combate');
  } catch (err) {
    const entry: CombatLogEntry = {
      id: nuevoLogId('spell-p'),
      ronda: state.ronda,
      actor: 'player',
      accion: `Hechizo: ${spell.nombre}`,
      resultado: (err as Error).message,
    };
    // Turno desperdiciado
    return { ...state, log: [...state.log, entry] };
  }

  const playerActualizado = {
    ...state.player,
    mana: Math.max(0, state.player.mana - spell.costoMana),
  };
  estadoConMana = { ...state, player: playerActualizado };

  // Efectos según el hechizo. Keep simple: hardcoded switch por id.
  switch (spell.id) {
    case 'susurro_rio': {
      const entry: CombatLogEntry = {
        id: nuevoLogId('spell-p'),
        ronda: state.ronda,
        actor: 'player',
        accion: 'Susurro del Río',
        resultado: 'El enemigo baja la guardia. Su daño se reducirá esta ronda.',
        delta: { manaPlayer: -spell.costoMana },
      };
      return {
        ...estadoConMana,
        player: {
          ...playerActualizado,
          efectosTemporales: [
            ...playerActualizado.efectosTemporales,
            {
              id: nuevoLogId('ef'),
              descripcion: 'Susurro activo',
              duracionRondas: 2,
              efecto: { kind: 'reduccionDanoEntrante', factor: 0.5 },
            },
          ],
        },
        log: [...state.log, entry],
      };
    }
    case 'vinculo_sanador': {
      const curado = 15;
      const nuevaVida = Math.min(playerActualizado.vidaMax, playerActualizado.vida + curado);
      useCharacterStore.getState().modificarStat('vida', nuevaVida - playerActualizado.vida);
      const entry: CombatLogEntry = {
        id: nuevoLogId('spell-p'),
        ronda: state.ronda,
        actor: 'player',
        accion: 'Vínculo Sanador',
        resultado: `Te sanás ${nuevaVida - playerActualizado.vida} de vida.`,
        delta: { vidaPlayer: nuevaVida - playerActualizado.vida, manaPlayer: -spell.costoMana },
      };
      return {
        ...estadoConMana,
        player: { ...playerActualizado, vida: nuevaVida },
        log: [...state.log, entry],
      };
    }
    case 'llamado_guardian': {
      const dano = tirarDano(18, 28);
      const nuevaVidaEnemy = Math.max(0, state.enemy.vidaActual - dano);
      const entry: CombatLogEntry = {
        id: nuevoLogId('spell-p'),
        ronda: state.ronda,
        actor: 'player',
        accion: 'Llamado del Guardián',
        resultado: `Un capibara ancestral golpea por vos: ${dano} de daño.`,
        delta: { vidaEnemy: -dano, manaPlayer: -spell.costoMana },
      };
      const nuevo = {
        ...estadoConMana,
        enemy: { ...state.enemy, vidaActual: nuevaVidaEnemy },
        log: [...state.log, entry],
      };
      if (nuevaVidaEnemy === 0) return finalizar(nuevo, 'victoria');
      return nuevo;
    }
    case 'velo_niebla': {
      const entry: CombatLogEntry = {
        id: nuevoLogId('spell-p'),
        ronda: state.ronda,
        actor: 'player',
        accion: 'Velo de Niebla',
        resultado: 'El aire se espesa. Escapás del combate.',
        delta: { manaPlayer: -spell.costoMana },
      };
      const nuevo = { ...estadoConMana, log: [...state.log, entry] };
      return finalizar(nuevo, 'huida');
    }
    case 'brasa_interior': {
      const dano = tirarDano(22, 34);
      const nuevaVidaEnemy = Math.max(0, state.enemy.vidaActual - dano);
      const entry: CombatLogEntry = {
        id: nuevoLogId('spell-p'),
        ronda: state.ronda,
        actor: 'player',
        accion: 'Brasa Interior',
        resultado: `Todo el fuego que contenías sale de golpe: ${dano} de daño.`,
        delta: { vidaEnemy: -dano, manaPlayer: -spell.costoMana },
      };
      const nuevo = {
        ...estadoConMana,
        enemy: { ...state.enemy, vidaActual: nuevaVidaEnemy },
        log: [...state.log, entry],
      };
      if (nuevaVidaEnemy === 0) return finalizar(nuevo, 'victoria');
      return nuevo;
    }
    default: {
      const entry: CombatLogEntry = {
        id: nuevoLogId('spell-p'),
        ronda: state.ronda,
        actor: 'player',
        accion: spell.nombre,
        resultado: 'El hechizo surte efecto, pero nada visible cambia en el combate.',
        delta: { manaPlayer: -spell.costoMana },
      };
      return { ...estadoConMana, log: [...state.log, entry] };
    }
  }
}

function resolverHuidaPlayer(state: CombatState): CombatState {
  const ventajaArtimana = useCharacterStore
    .getState()
    .personaje // usamos solo para chequear la flag; no es obligatorio
    ? 0
    : 0;
  void ventajaArtimana; // placeholder; la skill se chequea vía flag en world
  const mod = Math.floor(state.player.stats.astucia / 2) + 2;
  const check = tirarCheck(rngActual, mod, 12);
  const entry: CombatLogEntry = {
    id: nuevoLogId('huir-p'),
    ronda: state.ronda,
    actor: 'player',
    accion: 'Huir',
    resultado: check.exito
      ? 'Te soltás y corrés. El enemigo queda atrás.'
      : 'No podés alejarte. El enemigo te corta el paso.',
    roll: check,
  };
  const nuevo = { ...state, log: [...state.log, entry] };
  if (check.exito) return finalizar(nuevo, 'huida');
  return nuevo;
}

function resolverTurnoEnemigo(state: CombatState): CombatState {
  const accion = decidirAccionEnemigo(state, rngActual);

  switch (accion.kind) {
    case 'atacar':
      return resolverAtaqueEnemigo(state, false);
    case 'especial':
      return resolverAtaqueEnemigo(state, true);
    case 'defender': {
      const entry: CombatLogEntry = {
        id: nuevoLogId('def-e'),
        ronda: state.ronda,
        actor: 'enemy',
        accion: 'Defender',
        resultado: `${state.enemy.nombre} se cubre. +3 a su armadura.`,
      };
      return {
        ...state,
        enemy: { ...state.enemy, defensaTemp: state.enemy.defensaTemp + 3 },
        log: [...state.log, entry],
      };
    }
  }
}

function resolverAtaqueEnemigo(state: CombatState, esEspecial: boolean): CombatState {
  const { enemy, player } = state;
  const especial = esEspecial ? enemy.ai.especial : undefined;
  const dc = modDefensaPlayer(player);
  const check = tirarCheck(rngActual, enemy.modAtaque, dc);

  const nombreAccion = especial ? especial.nombre : 'Ataque';
  const entry: CombatLogEntry = {
    id: nuevoLogId(esEspecial ? 'esp-e' : 'atk-e'),
    ronda: state.ronda,
    actor: 'enemy',
    accion: nombreAccion,
    resultado: '',
    roll: check,
  };

  if (!check.exito) {
    entry.resultado = `${enemy.nombre}: ${
      check.crit === 'miss' ? 'falla feo y se expone.' : 'falla.'
    }`;
    return { ...state, log: [...state.log, entry] };
  }

  const rangoMin = especial ? especial.danoMin : enemy.danoMin;
  const rangoMax = especial ? especial.danoMax : enemy.danoMax;
  let dano = tirarDano(rangoMin, rangoMax);
  if (check.crit === 'hit') dano *= 2;

  // Reducciones por efectos (solo si NO es especial que ignora defensa)
  if (!esEspecial || enemy.ai.especial?.id !== 'embate_demoledor') {
    dano = aplicarReduccionDano(player, dano);
  }

  const nuevaVidaPlayer = Math.max(0, player.vida - dano);
  entry.resultado =
    check.crit === 'hit'
      ? `¡Crítico contra vos! ${dano} de daño.`
      : `${enemy.nombre} te golpea por ${dano}.`;
  entry.delta = { vidaPlayer: -dano };

  // Sincronizar vida en el store global.
  useCharacterStore.getState().modificarStat('vida', -dano);

  const nuevoEnemy = esEspecial && enemy.ai.especial
    ? { ...enemy, especialDisponibleEn: state.ronda + enemy.ai.especial.costoInterno }
    : enemy;

  const nuevoState: CombatState = {
    ...state,
    enemy: nuevoEnemy,
    player: { ...player, vida: nuevaVidaPlayer },
    log: [...state.log, entry],
  };

  if (nuevaVidaPlayer === 0) return finalizar(nuevoState, 'derrota');
  return nuevoState;
}

function finalizar(state: CombatState, estado: Exclude<CombatState['estado'], 'activo'>): CombatState {
  const xpGanada = estado === 'victoria' ? state.enemy.xpRecompensa : undefined;
  const resumen =
    estado === 'victoria'
      ? `Venciste a ${state.enemy.nombre}.`
      : estado === 'huida'
        ? 'Escapaste.'
        : 'Caíste en combate.';
  const entry: CombatLogEntry = {
    id: nuevoLogId('fin'),
    ronda: state.ronda,
    actor: estado === 'victoria' ? 'player' : 'enemy',
    accion: 'Fin',
    resultado: resumen,
  };
  return {
    ...state,
    estado,
    log: [...state.log, entry],
    xpGanada,
  };
}

export function aplicarRecompensasAlPersonaje(state: CombatState): void {
  if (state.estado !== 'victoria') return;
  if (state.xpGanada) useCharacterStore.getState().ganarXp(state.xpGanada);
  if (state.enemy.loot) {
    // Usamos el effect engine para unificar. Import tardío para evitar ciclo.
    for (const l of state.enemy.loot) {
      useCharacterStore.setState((s) => {
        const existente = s.personaje.inventario.find((i) => i.id === l.itemId);
        const inv = existente
          ? s.personaje.inventario.map((i) =>
              i.id === l.itemId ? { ...i, cantidad: i.cantidad + l.cantidad } : i,
            )
          : [
              ...s.personaje.inventario,
              {
                id: l.itemId,
                nombre: l.itemId,
                descripcion: '',
                tipo: 'narrativo' as const,
                cantidad: l.cantidad,
              },
            ];
        return { personaje: { ...s.personaje, inventario: inv } };
      });
    }
    useCharacterStore.getState().persistir();
  }
}

export type { CombatRollInfo };
